import React, { useState } from 'react';
import { Receipt, ChevronDown, ChevronRight, CreditCard, Banknote, Wallet, Tag } from 'lucide-react';
import AppleLogo from './icons/AppleLogo';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import ReceiptPrint from './ReceiptPrint';
import TabReceiptButton from './TabReceiptButton';
import { tableLabel } from '../lib/tableLabel';

/**
 * SettledTabGroupCard
 * ===================
 * Renders all orders that share a `tab_id` under ONE collapsible card on the
 * manager's Completed view (and any other "history" surface). The header
 * shows the customer name, settlement method, final total + discount, and
 * how many orders were on the tab. Tapping the header expands the orders
 * inline; each order keeps its own per-order receipt button. A "Tab
 * Receipt" CTA at the bottom of the header prints a single combined
 * receipt covering every order on the tab.
 *
 * Used by OrdersPage (view='completed') and Tables history. Pure
 * presentation — assumes orders are already filtered + sorted.
 */
const METHOD_ICON = { cash: Banknote, card: CreditCard, eftpos: Wallet, apple_pay: AppleLogo, mixed: Wallet };

function pickDominantMethod(orders) {
  // Most paid-tab orders share `final_payment_method`. Take the first non-empty one.
  for (const o of orders) {
    const m = o.final_payment_method;
    if (m) return m;
  }
  return null;
}

export default function SettledTabGroupCard({ tabId, orders, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  if (!orders || orders.length === 0) return null;

  const sumTotal = orders.reduce((s, o) => s + (Number(o.total) || 0), 0);
  const discountTotal = orders.reduce((s, o) => s + (Number(o.discount_amount) || 0), 0);
  // Pull display metadata off the first order — they were all on the same tab.
  const lead = orders[0];
  const customerName = lead.customer_name || 'Tab';
  const tableNumbers = [...new Set(orders.map(o => o.table_number).filter(Boolean))];
  const settledAt = orders.map(o => o.tab_settled_at).filter(Boolean).sort().pop();
  const method = pickDominantMethod(orders) || 'tab';
  const MethodIcon = METHOD_ICON[method] || Receipt;

  return (
    <div className="bg-white/[0.03] border border-blue-accent/30 rounded-sm overflow-hidden" data-testid={`tab-group-${tabId}`}>
      {/* Header — clickable summary */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-start justify-between gap-3 p-4 hover:bg-white/[0.02] transition-colors text-left"
        data-testid={`tab-group-${tabId}-toggle`}
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-12 h-12 rounded-sm bg-blue-accent/10 border border-blue-accent/40 flex flex-col items-center justify-center shrink-0">
            <Receipt className="w-4 h-4 text-blue-accent" />
            <span className="text-[8px] uppercase tracking-[0.18em] text-blue-accent font-sans font-bold mt-0.5">Tab</span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-serif text-lg text-white truncate">{customerName}</span>
              <Badge className="bg-emerald-500/15 text-emerald-300 border-0 font-sans text-[10px] uppercase tracking-[0.1em]">
                <MethodIcon className="w-3 h-3 mr-1 inline" />{method === 'apple_pay' ? 'Apple Pay' : method}
              </Badge>
              {discountTotal > 0 && (
                <Badge className="bg-pink-500/15 text-pink-300 border-0 font-sans text-[10px] uppercase tracking-[0.1em]">
                  <Tag className="w-3 h-3 mr-1 inline" />−${discountTotal.toFixed(2)}
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-zinc-500 font-sans mt-0.5 truncate" data-testid={`tab-group-${tabId}-meta`}>
              {orders.length} order{orders.length === 1 ? '' : 's'}
              {tableNumbers.length ? ` · T${tableNumbers.join(', T')}` : ''}
              {settledAt ? ` · settled ${new Date(settledAt).toLocaleString('en-AU', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}` : ''}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <span className="text-gold font-serif font-bold text-lg block leading-none" data-testid={`tab-group-${tabId}-total`}>${sumTotal.toFixed(2)}</span>
          <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans flex items-center gap-1 mt-1 justify-end">
            {expanded ? <><ChevronDown className="w-3 h-3" /> Hide</> : <><ChevronRight className="w-3 h-3" /> View</>}
          </span>
        </div>
      </button>

      {/* Tab Receipt CTA — always visible, even when collapsed, so manager
          doesn't have to expand to reprint the combined receipt. */}
      <div className="border-t border-white/5 px-4 py-2 flex justify-end" data-testid={`tab-group-${tabId}-actions`}>
        <TabReceiptButton tabId={tabId} />
      </div>

      {/* Expanded — show individual orders. Each keeps its own per-order
          receipt so the manager still has the per-order audit trail. */}
      {expanded && (
        <div className="border-t border-white/5 px-4 pb-4 pt-3 space-y-2" data-testid={`tab-group-${tabId}-orders`}>
          {orders.map(o => (
            <div key={o.id} className="rounded-sm border border-white/8 bg-black/30 p-3" data-testid={`tab-group-order-${o.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="min-w-0">
                  <span className="text-zinc-500 text-[10px] font-mono">#{o.id.slice(0, 8)}</span>
                  <span className="text-zinc-400 text-[11px] font-sans ml-2">{tableLabel(o.table_number)}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-gold font-sans font-semibold text-sm">${Number(o.total).toFixed(2)}</span>
                  <ReceiptPrint orderId={o.id} />
                </div>
              </div>
              <div className="space-y-0.5">
                {(o.items || []).map(item => (
                  <div key={item.id} className="flex items-center justify-between gap-2 text-[11px] text-zinc-400 font-sans">
                    <span className="truncate">{item.quantity}× {item.name}</span>
                    <span className="text-zinc-600 font-mono shrink-0">${Number(item.total ?? (item.price * item.quantity)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
